/**
 * 🛒 RetailCabinet — 超商／量販柜
 * 資料：主權編譯 retail_store_v1；bbox 載入，品牌多選。
 */

import { SovereignCabinet } from '../../core/cabinet-base.js';
import { appendMapClientQuery, isCompactMapClient } from '../../utils/map-client-profile.js';
import { googleMapsNavButtonHtml } from '../../utils/google-maps-nav.js';

const RETAIL_MIN_ZOOM_COMPACT = 12;

const BRAND_SHORT = {
    '711': '7-11',
    FAMILYMART: '全家',
    HILIFE: '萊爾富',
    OKMART: 'OK',
    PXMART: '全聯'
};

function esc(s) {
    return String(s ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

class RetailCabinet extends SovereignCabinet {
    constructor() {
        super();
        this.layer = null;
        this._fc = null;
        this.brandFilters = new Set();
        this._viewportTimer = null;
        this.loadAssets('/js/cabinets/retail');
    }

    onMount() {
        this.ensureGlobalStyles();
        const toggle = this.shadowRoot.getElementById('toggleRetail');

        toggle?.addEventListener('change', (e) => {
            if (e.target.checked) {
                if (!this.brandFilters.size) {
                    this.shadowRoot.querySelectorAll('[data-retail-brand]').forEach((el) => {
                        el.checked = true;
                    });
                    this.syncBrandFilters();
                }
                this.loadAndRender();
            } else {
                this.clearLayer();
                this.clearBrandChecks();
            }
            this.syncSubToggleState();
        });

        this.shadowRoot.querySelectorAll('[data-retail-brand]').forEach((el) => {
            el.addEventListener('change', () => {
                this.syncBrandFilters();
                if (toggle?.checked) this.loadAndRender();
            });
        });
    }

    afterAssetsMounted() {
        this.syncSubToggleState();
    }

    onMapReady(map) {
        this.map = map;
        this.ensureGlobalStyles();
        const onViewport = () => {
            const on = !!this.shadowRoot?.getElementById('toggleRetail')?.checked;
            if (!on) return;
            clearTimeout(this._viewportTimer);
            this._viewportTimer = setTimeout(() => this.loadAndRender(), 280);
        };
        this.map.on('moveend', onViewport);
        this.map.on('zoomend', onViewport);
    }

    onUnmount() {
        this.clearLayer();
    }

    ensureGlobalStyles() {
        if (document.querySelector('link[data-retail-map-css]')) return;
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = '/css/retail-map-overlays.css?v=17.0.817';
        link.setAttribute('data-retail-map-css', '17.0.817');
        document.head.appendChild(link);
    }

    syncSubToggleState() {
        const on = !!this.shadowRoot?.getElementById('toggleRetail')?.checked;
        this.shadowRoot?.querySelectorAll('[data-retail-brand]').forEach((el) => {
            el.disabled = !on;
            if (!on) el.checked = false;
        });
        if (!on) this.brandFilters.clear();
    }

    syncBrandFilters() {
        this.brandFilters = new Set(
            [...(this.shadowRoot?.querySelectorAll('[data-retail-brand]:checked') || [])]
                .map((el) => el.getAttribute('data-retail-brand'))
                .filter(Boolean)
                .map((id) => String(id).toUpperCase())
        );
    }

    clearBrandChecks() {
        this.shadowRoot?.querySelectorAll('[data-retail-brand]').forEach((el) => {
            el.checked = false;
        });
        this.brandFilters.clear();
    }

    setStatus(text, { hint = false } = {}) {
        const el = this.shadowRoot?.getElementById('status-text');
        if (!el) return;
        el.innerText = text;
        el.classList.toggle('is-hint', !!hint);
    }

    bboxQuery() {
        const bounds = this.map.getBounds().pad(0.12);
        const sw = bounds.getSouthWest();
        const ne = bounds.getNorthEast();
        const params = new URLSearchParams({
            xmin: String(sw.lng),
            ymin: String(sw.lat),
            xmax: String(ne.lng),
            ymax: String(ne.lat),
            zoom: String(this.map.getZoom())
        });
        if (this.brandFilters.size) {
            params.set('brand', [...this.brandFilters].join(','));
        }
        return appendMapClientQuery(params);
    }

    markerHtml(props) {
        const brandId = String(props.h_brand_id || '').toUpperCase();
        const short = BRAND_SHORT[brandId] || brandId.slice(0, 4) || '?';
        return `<div class="retail-mark" title="${esc(props.h_name || '')}">
            <span class="retail-badge brand-${esc(brandId)}">${esc(short)}</span>
        </div>`;
    }

    markerIcon(props) {
        return L.divIcon({
            className: 'custom-retail-mark',
            html: this.markerHtml(props),
            iconSize: [48, 18],
            iconAnchor: [12, 9]
        });
    }

    buildPopupHtml(props, latlng = null) {
        const name = esc(props.h_name || '零售點');
        const addr = esc(props.h_addr || '');
        const brand = esc(props.h_brand_name || props.h_brand_id || '');
        const klass = esc(props.h_retail_class === 'supermarket' ? '量販／超市' : '便利超商');
        const navBtn = googleMapsNavButtonHtml(latlng || props);
        return `
            <div class="retail-popup-inner">
                <div class="retail-popup-kicker">🛒 超商／量販</div>
                <div class="retail-title">${name}</div>
                ${brand ? `<div class="retail-meta"><b>品牌：</b>${brand}</div>` : ''}
                <div class="retail-meta"><b>類型：</b>${klass}</div>
                ${addr ? `<div class="retail-meta">📍 ${addr}</div>` : ''}
                ${navBtn}
                <div class="retail-popup-source" style="margin-top:6px">來源：經濟部商工開放資料（主權重編）</div>
            </div>
        `;
    }

    clearLayer(updateStatus = true) {
        if (this.map && this.layer) {
            this.map.removeLayer(this.layer);
            this.layer = null;
        }
        this._fc = null;
        if (updateStatus) this.setStatus('已隱藏');
    }

    mountGeoJson(fc) {
        if (!this.map) return;
        this.clearLayer(false);
        const features = Array.isArray(fc?.features) ? fc.features : [];
        const geo = L.geoJSON(
            { type: 'FeatureCollection', features },
            {
                pointToLayer: (feature, latlng) =>
                    L.marker(latlng, { icon: this.markerIcon(feature.properties || {}) }),
                onEachFeature: (feature, layer) => {
                    layer.bindPopup(this.buildPopupHtml(feature.properties || {}, feature), {
                        className: 'retail-popup',
                        maxWidth: 320
                    });
                }
            }
        );
        if (isCompactMapClient() && typeof L.markerClusterGroup === 'function') {
            const cluster = L.markerClusterGroup({
                maxClusterRadius: 48,
                disableClusteringAtZoom: 16,
                spiderfyOnMaxZoom: true,
                showCoverageOnHover: false
            });
            cluster.addLayer(geo);
            cluster.addTo(this.map);
            this.layer = cluster;
        } else {
            geo.addTo(this.map);
            this.layer = geo;
        }
    }

    ensureMarkerClusterLib() {
        if (this._clusterLibsLoaded && typeof L?.markerClusterGroup === 'function') {
            return Promise.resolve();
        }
        return new Promise((resolve, reject) => {
            if (!document.querySelector('link[data-retail-mc-css="1"]')) {
                for (const href of [
                    '/lib/markercluster/MarkerCluster.css',
                    '/lib/markercluster/MarkerCluster.Default.css'
                ]) {
                    const link = document.createElement('link');
                    link.rel = 'stylesheet';
                    link.href = href;
                    link.setAttribute('data-retail-mc-css', '1');
                    document.head.appendChild(link);
                }
            }
            if (typeof L?.markerClusterGroup === 'function') {
                this._clusterLibsLoaded = true;
                resolve();
                return;
            }
            const script = document.createElement('script');
            script.src = '/lib/markercluster/leaflet.markercluster.js';
            script.onload = () => {
                this._clusterLibsLoaded = true;
                resolve();
            };
            script.onerror = () => reject(new Error('markercluster load failed'));
            document.head.appendChild(script);
        });
    }

    parseStoresResponse(res, body) {
        if (!res.ok) {
            const code = body?.error || `HTTP ${res.status}`;
            if (res.status === 404 || code === 'RETAIL_STORES_NOT_FOUND') {
                throw new Error('零售熱檔尚未掛載');
            }
            throw new Error(typeof code === 'string' ? code : `HTTP ${res.status}`);
        }
        if (!body || typeof body !== 'object' || !Array.isArray(body.features)) {
            throw new Error('回應格式無效（非 FeatureCollection）');
        }
        return body;
    }

    async loadAndRender() {
        if (!this.map) return;
        if (isCompactMapClient() && this.map.getZoom() < RETAIL_MIN_ZOOM_COMPACT) {
            this.clearLayer(false);
            this.setStatus(`請放大至 zoom ${RETAIL_MIN_ZOOM_COMPACT}+`, { hint: true });
            return;
        }
        this.syncBrandFilters();
        if (!this.brandFilters.size) {
            this.clearLayer(false);
            this.setStatus('請至少勾選一個品牌', { hint: true });
            return;
        }
        this.setStatus('載入中…');
        try {
            if (isCompactMapClient()) {
                await this.ensureMarkerClusterLib().catch((e) =>
                    console.warn('[RetailCabinet] markercluster unavailable', e)
                );
            }
            const qs = this.bboxQuery();
            const res = await fetch(`/api/live/retail/stores?${qs}`, {
                credentials: 'include',
                cache: 'default'
            });
            let body = null;
            try {
                body = await res.json();
            } catch {
                body = null;
            }
            this._fc = this.parseStoresResponse(res, body);
            this.mountGeoJson(this._fc);
            const n = this._fc?.features?.length || 0;
            const brandHint = [...this.brandFilters].map((id) => BRAND_SHORT[id] || id).join('+');
            const trunc = this._fc?._stats?.truncated ? ' · 已截斷' : '';
            this.setStatus(`✅ ${n} 點（${brandHint}）${trunc}`, { hint: !!trunc });
        } catch (err) {
            this.clearLayer(false);
            this.setStatus(`⚠️ 載入失敗：${err.message || err}`, { hint: true });
        }
    }
}

customElements.define('retail-cabinet', RetailCabinet);

function dock() {
    if (!window.appCore) return;
    const container = document.getElementById('socket-retail');
    if (!container) return;
    const instance = document.createElement('retail-cabinet');
    container.innerHTML = '';
    container.appendChild(instance);
    window.appCore.registerCabinet('retail', instance);
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', dock);
} else {
    dock();
}
